import React, { useState } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, CalendarCheck, CheckCircle2 } from 'lucide-react';
import { Button } from '../components';
import { GespreksverslagEngineForm } from '../components/GespreksverslagEngineForm';
import { getVerslagContext, stuurToestemmingSms, type VerslagResultaat } from '../services/gespreksverslagApi';

/** Deel 5-toestemming per SMS; het nummer wordt alleen voor dit bericht gebruikt. */
const ToestemmingSms: React.FC<{ verslagId: string }> = ({ verslagId }) => {
  const [telefoon, setTelefoon] = useState('');
  const sms = useMutation({
    mutationFn: () => stuurToestemmingSms(verslagId, telefoon),
    onSuccess: (r) => toast.success(`Toestemmingsvraag verstuurd naar ${r.verstuurd_naar}`),
    onError: (e: any) => toast.error(e?.response?.data?.message || 'De SMS kon niet worden verstuurd'),
  });
  if (sms.isSuccess) return <p className="text-sm text-green-700">Toestemmingsvraag verstuurd naar {sms.data.verstuurd_naar}.</p>;
  return (
    <div className="flex flex-wrap items-end gap-2 text-sm">
      <label className="block">
        <span className="text-gray-700">Mobiel nummer van de medewerker</span>
        <input type="tel" inputMode="tel" autoComplete="off" placeholder="06 12345678" className="mt-1 block w-56 rounded-md border-gray-300" value={telefoon} onChange={(e) => setTelefoon(e.target.value)} />
      </label>
      <Button variant="outline" disabled={telefoon.trim().length < 10} loading={sms.isPending} onClick={() => sms.mutate()}>Stuur toestemmingsvraag</Button>
    </div>
  );
};

/**
 * Gespreksverslag vanuit een agenda-afspraak (route 1 en 2, migratie 176).
 * Zonder afspraak geen verslag: een gesprek zonder afspraak is een ad-hoc melding (route 3).
 */
export const ProfessionalCreateVerslagPage: React.FC = () => {
  const { t } = useTranslation('pages');
  const { dossierId, id } = useParams<{ dossierId?: string; id?: string }>();
  const navigate = useNavigate();
  const actualDossierId = dossierId || id;
  const afspraakId = useSearchParams()[0].get('afspraak');
  const [resultaat, setResultaat] = useState<VerslagResultaat | null>(null);

  const { data: ctx, isLoading, error } = useQuery({
    queryKey: ['verslag-context', afspraakId],
    queryFn: () => getVerslagContext({ afspraak_id: afspraakId as string }),
    enabled: !!afspraakId,
    retry: false,
  });

  const terug = actualDossierId ? `/professional/dossiers/${actualDossierId}` : '/professional/agenda';
  const kader = (inhoud: React.ReactNode) => (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Link to={terug}>
        <Button variant="outline" className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('professionalCreateVerslag.backToDossier')}
        </Button>
      </Link>
      {inhoud}
    </div>
  );
  const melding = (tekst: string, extra?: React.ReactNode) =>
    kader(
      <div className="text-center py-12">
        <p className="text-gray-600">{tekst}</p>
        {extra}
      </div>
    );

  if (!afspraakId) {
    return melding(
      'Een gespreksverslag hoort altijd bij een registratienummer. Open het verslag vanuit de afspraak in je agenda; een gesprek zonder afspraak registreer je als ad-hoc melding.',
      <div className="mt-4 flex justify-center gap-4 text-sm">
        <Link to="/professional/agenda" className="text-blue-600 hover:underline">Naar de agenda →</Link>
        <Link to="/professional/adhoc-melding" className="text-blue-600 hover:underline">Ad-hoc melding registreren →</Link>
      </div>
    );
  }
  if (isLoading) return melding(t('professionalCreateVerslag.loading'));
  if (error || !ctx) return melding((error as any)?.response?.data?.message || 'Deze afspraak kon niet worden geladen.');
  if (ctx.bestaand_verslag_id) return melding('Voor deze afspraak bestaat al een gespreksverslag.');

  if (resultaat) {
    return kader(
      <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-3">
        <div className="flex items-center gap-2 text-green-700">
          <CheckCircle2 className="w-6 h-6" aria-hidden />
          <h1 className="text-xl font-semibold">Gespreksverslag opgeslagen</h1>
        </div>
        <ul className="text-sm text-gray-700 space-y-1">
          {resultaat.case_code && <li>Casus <span className="font-mono">{resultaat.case_code}</span> — telt mee in het jaarverslag na uw audit.</li>}
          {resultaat.deel5_toestemming === 'portaal' && <li>De medewerker krijgt in het portaal het verzoek om de management-update met HR te delen.</li>}
          {resultaat.deel5_toestemming === 'email' && <li>De medewerker ontvangt per e-mail het verzoek om de management-update met HR te delen.</li>}
          {resultaat.deel5_toestemming === 'geen_kanaal' && (
            <li className="text-amber-800">De medewerker heeft geen portaal of e-mailadres. Stuur de toestemmingsvraag hieronder per SMS; tot dan ziet HR niets.</li>
          )}
          {resultaat.verlenging_status === 'PENDING_HR_AUTHORIZATION' && (
            <li className="text-amber-800">Verlengingsverzoek verstuurd. De agenda blokkeert nieuwe boekingen in dit traject tot HR akkoord geeft.</li>
          )}
        </ul>
        {resultaat.deel5_toestemming === 'geen_kanaal' && <ToestemmingSms verslagId={resultaat.verslag_id} />}
        <Button onClick={() => navigate(terug)}>Terug</Button>
      </div>
    );
  }

  return kader(
    <>
      {ctx.afspraak && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
          <CalendarCheck className="w-4 h-4 mt-0.5 shrink-0" aria-hidden />
          <span>
            Gekoppeld aan de afspraak van{' '}
            {new Date(ctx.afspraak.start_tijd).toLocaleString('nl-NL', { dateStyle: 'long', timeStyle: 'short' })}
            {ctx.registratienummer && <> · registratienummer <span className="font-mono">{ctx.registratienummer}</span></>}
          </span>
        </div>
      )}
      <GespreksverslagEngineForm context={ctx} afspraakId={afspraakId} onSuccess={setResultaat} onCancel={() => navigate(terug)} />
    </>
  );
};
